import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { InfoBox, InfoWrapper, RHFNumberTextField } from 'components';
import { FenomenoIspezioneSpecialeIdraulico } from 'constants/inspections';
import { useInspectionPath } from 'hooks';
import { INFO } from 'pages/Inspections/config/info';
import { useInspectionSelector } from 'stores';

const Accordion3OvertoppingL3 = () => {
  const { watch, setValue, formState } = useFormContext();
  const { dirtyFields } = formState;
  // e-2026-06-t21 — stesso pattern di L0/L1, vedi Accordion1GeneralInfoL3.jsx.
  const { inspectionDisabled } = useInspectionSelector();
  const { isViewingInspection } = useInspectionPath();
  const readOnly = inspectionDisabled || isViewingInspection;

  // Se Sormonto non è tra i fenomeni scelti in "Analisi L3 - Fenomeno
  // analizzato" (Accordion1GeneralInfoL3.jsx, 577), i campi di questo
  // accordion vanno disabilitati.
  const watchAnalisiFenomenoAnalizzato = watch('analisiFenomenoAnalizzato');
  const hasSormonto = watchAnalisiFenomenoAnalizzato?.includes(
    FenomenoIspezioneSpecialeIdraulico.Sormonto
  );

  // Issue #1021, punto 20 — 141 (francoIdraulicoFP2) è "Autocompilazione"
  // (dati-piattaforma-campi.md, ID141: "Pari alla differenza tra 'Quota
  // minima intradosso del ponte' e Quota del pelo libero (scenario P2)",
  // formula ID134-ID138), non un prefill one-shot: va ricalcolato ad ogni
  // cambio di quotaMinimaIntradosso (134, questo accordion) o quotaPeloLiberoP2
  // (138, Accordion2HydraulicDataL3.jsx — stesso form, cross-accordion watch
  // come altrove in questo file). Stesso identico calcolo già in uso in L1
  // (Accordion4HydraulicRiskL1.jsx).
  const watchQuotaMinimaIntradosso = watch('quotaMinimaIntradosso');
  const watchQuotaPeloLiberoP2 = watch('quotaPeloLiberoP2');
  // Issue #1021, riapertura 2026-09-21, punto 13 — terzo caso dello stesso
  // bug "banner modifiche non salvate persiste dopo il salvataggio" (i primi
  // due, risolti il 2026-09-02, sono nel commento storico di
  // mappingInL3.js). Qui la guardia era sull'isDirty di TUTTO il form L3:
  // selezionare un valore in "Analisi L3 - Fenomeno analizzato" (577,
  // Accordion1) dirtava il form e riattivava QUESTO effetto anche se
  // quotaMinimaIntradosso/quotaPeloLiberoP2 non erano mai stati toccati —
  // ricalcolando francoIdraulicoFP2 con `.toFixed(2)` da un valore ereditato
  // che invece L1 (Accordion4HydraulicRiskL1.jsx) salva a piena precisione
  // (nessun arrotondamento): un cambiamento di valore REALE, mai richiesto
  // dall'utente, che poi il salvataggio persiste. Guardia sostituita con
  // formState.dirtyFields dei due soli campi sorgente di QUESTO calcolo:
  // resta false per un cambiamento altrove nel form (577) e per il reset()
  // post-salvataggio (che azzera dirtyFields), scatta solo per una modifica
  // reale a uno dei due campi sorgente.
  useEffect(() => {
    if (!dirtyFields.quotaMinimaIntradosso && !dirtyFields.quotaPeloLiberoP2) {
      return;
    }
    const quotaMinima = parseFloat(watchQuotaMinimaIntradosso);
    const quotaPeloP2 = parseFloat(watchQuotaPeloLiberoP2);

    if (!isNaN(quotaMinima) && !isNaN(quotaPeloP2)) {
      // Bug "banner modifiche non salvate riappare subito dopo il
      // salvataggio" (utente, 2026-09-02) — .toFixed(2) da solo restituisce
      // una STRINGA ("12.35"), ma dopo il salvataggio mappingInL3.js
      // ripropaga il valore così com'è tornato dal BE, un NUMERO JSON
      // (12.35): stesso valore, tipo diverso — react-hook-form li considera
      // dirty (`"12.35" !== 12.35`) anche se numericamente identici. Number()
      // in più mantiene il tipo coerente col baseline stabilito da reset().
      setValue(
        'francoIdraulicoFP2',
        Number((quotaMinima - quotaPeloP2).toFixed(2))
      );
    } else {
      setValue('francoIdraulicoFP2', '');
    }
  }, [
    watchQuotaMinimaIntradosso,
    watchQuotaPeloLiberoP2,
    dirtyFields.quotaMinimaIntradosso,
    dirtyFields.quotaPeloLiberoP2,
  ]);

  return (
    <Grid container spacing={2} mt={2}>
      {!hasSormonto && (
        <Grid item xs={12}>
          <InfoBox
            color="warning.light"
            text={
              <>
                {
                  'Per poter modificare i campi del fenomeno Sormonto è necessario selezionare la voce '
                }
                <b>{'Sormonto'}</b>
                {' nel campo '}
                <b>{'Fenomeno analizzato'}</b>
                {" all'interno della sezione "}
                <b>{'L3 - Idraulica > 1. Informazioni Generali'}</b>
              </>
            }
          />
        </Grid>
      )}
      <InfoWrapper
        size={3}
        infoDrawer={true}
        drawerTitle={INFO.quotaMinimaIntradossoRischioIdraulico.drawerTitle}
        drawerText={INFO.quotaMinimaIntradossoRischioIdraulico.drawerText}
      >
        <RHFNumberTextField
          name="quotaMinimaIntradosso"
          label={'Quota minima intradosso del ponte [m]*'}
          defaultValue=""
          decimalScale={2}
          withValueLimit={({ value }) => value < 10000 && value > -10000}
          disabled={!hasSormonto || readOnly}
        />
      </InfoWrapper>
      <InfoWrapper size={3} infoMessage={INFO.spessoreImpalcato.infoMessage}>
        <RHFNumberTextField
          name="spessoreImpalcato"
          label={'Spessore impalcato [m]'}
          decimalScale={2}
          defaultValue=""
          // Issue #1021, punto 19 — "Numerico (-f6.2)" (ID588,
          // dati-piattaforma-campi.md), stesso formato di quotaMinimaIntradosso
          // sopra.
          withValueLimit={({ value }) => value < 10000 && value > -10000}
          disabled={!hasSormonto || readOnly}
        />
      </InfoWrapper>

      {/* Issue #1021, riapertura 2026-09-04/09-21, punto 17/22 — stesso
      pattern trovato altrove (Accordion1GeneralInfoL3.jsx,
      Accordion2HydraulicDataL3.jsx): non wrappato in InfoWrapper come
      quotaMinimaIntradosso/spessoreImpalcato sulla stessa riga, restava 5px
      più in alto. Nessun testo info esiste per questo campo. */}
      <InfoWrapper size={3}>
        <RHFNumberTextField
          name="francoIdraulicoFP2"
          label={
            <>
              Franco idraulico F<sub style={{ fontSize: 12 }}>P2</sub> [m]*
            </>
          }
          decimalScale={2}
          defaultValue=""
          // Issue #1021, punto 19 — "Numerico (-f6.2)" (ID141).
          withValueLimit={({ value }) => value < 10000 && value > -10000}
          // Issue #1021, punto 20 — sempre disabled: campo "Autocompilazione"
          // (vedi effetto sopra), stesso trattamento del gemello L1
          // (Accordion4HydraulicRiskL1.jsx, francoIdraulicoFP2 sempre
          // `disabled`, non condizionato a inspectionDisabled/readOnly).
          disabled
        />
      </InfoWrapper>
    </Grid>
  );
};

export { Accordion3OvertoppingL3 };
